import React, { ReactNode, useMemo } from 'react'
import { PopoverProps } from 'core/components/dialogs/Popover'
import Button from 'core/components/buttons/Button'
import cn from 'classnames'
import Tooltip from 'core/components/dialogs/Tooltip'
import Icon from 'core/components/icons/Icon'

interface Props extends Omit<PopoverProps, 'show' | 'content'> {
  title?: string | React.ReactElement
  confirmButton?: string | React.ReactElement
  cancelButton?: string | React.ReactElement
  icon?: ReactNode | null
  className?: string
  disabled?: boolean
  onConfirm?: VoidFunction
}

const PopConfirm = ({
  title = 'Are you sure?',
  confirmButton = 'Yes',
  cancelButton = 'No',
  position = 'left-middle',
  icon = null,
  className,
  onConfirm = console.log,
  disabled = false,
  children,
  ...props
}: Props) => {
  const content = useMemo(() => {
    return (
      <div className={cn(['bg-white rounded-md py-4 px-4 shadow', className])}>
        <div className="flex items-center gap-3 mb-2">
          <span>
            {icon ?? (
              <Icon
                name="exclamation-mark"
                size={9}
                className="text-white bg-warning-2 p-1 rounded-full mr-4"
              />
            )}
          </span>
          {title}
        </div>
        <div className="flex items-center justify-end gap-3 w-full">
          {typeof cancelButton === 'string' ? (
            <Button variant="outline" className="text-sm !px-3">
              {cancelButton}
            </Button>
          ) : (
            cancelButton
          )}

          {typeof confirmButton === 'string' ? (
            <Button
              variant="primary"
              className="text-sm !px-3"
              onClick={onConfirm}
            >
              {confirmButton}
            </Button>
          ) : (
            <div onClick={onConfirm}>{confirmButton}</div>
          )}
        </div>
      </div>
    )
  }, [className, icon, title, cancelButton, confirmButton, onConfirm])

  return (
    <Tooltip
      dismissOnInteraction
      content={content}
      position={position}
      disabled={disabled}
      wrapperClassName="leading-normal"
      {...props}
      triggerMode="click"
    >
      {children}
    </Tooltip>
  )
}

export default PopConfirm
